import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import SEO from '@/components/SEO';
import SeoBreadcrumbs from '@/components/SeoBreadcrumbs';
import { ArrowRight, CheckCircle, Clock, Upload, FileText, Pencil } from 'lucide-react';

const FloorPlanFromSketch = () => {
  const breadcrumbs = [
    { name: "Home", path: "/" },
    { name: "Floor Plan from Sketch", path: "/floor-plan-from-sketch" }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        pageId="floor-plan-from-sketch"
        defaultTitle="Floor Plan from Sketch | House Plan Direct"
        defaultDescription="Convert your hand-drawn sketch into a professional floor plan. Perfect for property listings, house sales, and rental marketing. From £40, 24-48 hour delivery."
        aiSummary="House Plan Direct converts hand-drawn sketches, photos, and old plans into professional floor plans for property marketing. Simply upload your sketch and receive a listing-ready floor plan within 24-48 hours. Perfect for estate agents, landlords, and property sellers. Plans from £40."
        keywords="floor plan from sketch, convert sketch to floor plan, sketch to floor plan UK, hand drawn to floor plan, floor plan conversion service"
      />
      <SeoBreadcrumbs items={breadcrumbs} />
      <Header />
      
      {/* Hero Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-indigo-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center mb-4">
            <Pencil className="h-12 w-12 text-indigo-600" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            Floor Plan from Sketch
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Convert your hand-drawn sketch into a professional, listing-ready floor plan.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <span className="flex items-center bg-white px-4 py-2 rounded-full shadow-sm text-sm">
              <Upload className="h-4 w-4 text-indigo-600 mr-2" /> Upload any sketch
            </span>
            <span className="flex items-center bg-white px-4 py-2 rounded-full shadow-sm text-sm">
              <Clock className="h-4 w-4 text-blue-600 mr-2" /> 24-48 hour delivery
            </span>
            <span className="flex items-center bg-white px-4 py-2 rounded-full shadow-sm text-sm">
              <FileText className="h-4 w-4 text-green-600 mr-2" /> From £40
            </span>
          </div>
          <Link to="/order">
            <Button size="lg" className="bg-indigo-600 hover:bg-indigo-700">
              Upload Your Sketch <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
          <p className="text-sm text-gray-500 mt-4">
            Prefer to draw it yourself? <Link to="/floor-plan-creator" className="text-indigo-600 underline hover:text-indigo-700" data-testid="sketch-link-floor-plan-creator">Use the free Floor Plan Creator</Link> instead.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Convert Your Sketch to a Professional Floor Plan</h2>
          <p className="text-gray-600 mb-8">
            Don't have a professional floor plan? No problem. Simply measure your property, create a rough sketch 
            on paper, and upload it to our service. We'll convert your sketch into a clean, professional floor plan 
            suitable for Rightmove, Zoopla, property brochures, and marketing materials.
          </p>

          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-8 mb-12">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">What We Accept</h3>
            <ul className="space-y-3">
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-indigo-600 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700"><strong>Hand-drawn sketches</strong> – Even rough drawings work</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-indigo-600 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700"><strong>Photos of floor plans</strong> – Snap a picture of any existing plan</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-indigo-600 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700"><strong>Old floor plans</strong> – Outdated or faded plans can be redrawn</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-indigo-600 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700"><strong>Scanned PDFs</strong> – Any digital format accepted</span>
              </li>
            </ul>
          </div>

          {/* Process */}
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Simple 3-Step Process</h3>
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {[
              { step: "1", title: "Measure & Sketch", desc: "Measure your property and create a rough sketch showing room layout" },
              { step: "2", title: "Upload", desc: "Upload your sketch, photo, or old plan through our website" },
              { step: "3", title: "Receive", desc: "Get your professional floor plan in JPEG, PDF & DWG within 24-48 hours" }
            ].map((item, index) => (
              <div key={index} className="text-center p-6 bg-gray-50 rounded-xl">
                <div className="w-10 h-10 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">{item.title}</h4>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="bg-indigo-600 text-white rounded-xl p-8 text-center">
            <h3 className="text-2xl font-bold mb-4">Ready to Convert Your Sketch?</h3>
            <p className="text-indigo-100 mb-6">Upload your sketch and receive a professional floor plan within 24-48 hours.</p>
            <Link to="/order">
              <Button size="lg" variant="secondary" className="bg-white text-indigo-600 hover:bg-gray-100">
                Upload Your Sketch <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FloorPlanFromSketch;
